import { Checkbox, Group, Stack, TextInput } from "@mantine/core"
import { useForm } from "@mantine/form"
import { modals } from "@mantine/modals"
import { useTranslation } from "react-i18next"
import { useAPIResource } from "@/hooks"
import type { TSession } from "@/types"
import { dayjs, sessionsAPI } from "@/utils"
import { isOver, isStarted } from "@/utils/session"
import { ModalControls, SelectVisibility } from "../controls"
import { DateTimeInput } from "../forms"

interface IProps {
  session: TSession
}

export function SessionEdit({ session }: IProps) {
  const { t } = useTranslation()
  const { update } = useAPIResource(sessionsAPI, { id: session.id })
  const sessionIsOver = isOver(session)

  const form = useForm({
    initialValues: {
      title: session.title,
      dtstart: session.dtstart,
      dtfinish: session.dtfinish,
      visibility: session.visibility,
      is_anonymous: session.is_anonymous,
      has_peer_report: session.has_peer_report,
    },
    validate: (values) => ({
      dtstart: values.dtstart ? null : t("Please choose a date and time"),
      dtfinish: values.dtfinish && values.dtfinish > values.dtstart ? null : t("End date must be after start date"),
    }),
  })

  if (!isStarted(session)) return null

  const dtnow = Date.now()

  return (
    <Stack>
      <TextInput {...form.getInputProps("title")} label={t("Title")} />
      <Group w="100%" grow>
        <DateTimeInput
          {...form.getInputProps("dtstart")}
          label={t("Start Date")}
          minDate={dayjs(dtnow).startOf("day")}
          disabled={session.dtstart < dtnow}
        />
        <DateTimeInput
          {...form.getInputProps("dtfinish")}
          label={t("End Date")}
          disabled={sessionIsOver}
          minDate={dayjs(Math.max(form.values.dtstart, dtnow)).startOf("day").add(1, "day")}
        />
      </Group>
      <SelectVisibility {...form.getInputProps("visibility")} />
      {session.type === "feedback" && (
        <Checkbox
          label={t("Show anonymized results to reviewees")}
          styles={{ label: { maxWidth: 300 } }}
          {...form.getInputProps("has_peer_report", { type: "checkbox" })}
        />
      )}
      <ModalControls
        disabled={!form.isValid()}
        onSubmit={form.onSubmit(async (values) => {
          const dirty = form.getDirty()
          const dirtyValues = Object.fromEntries(Object.entries(values).filter(([key]) => dirty[key]))
          if (Object.keys(dirtyValues).length > 0) update({ id: session.id, ...dirtyValues })
          modals.closeAll()
        })}
      ></ModalControls>
    </Stack>
  )
}
